import type { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js";
import type {
  Action, ActionDraft, ActionMoveDestination, DashboardData, DataRepository, DailyNote, Memo, MemoDraft, Ticket, TicketCatalog, TicketDraft, TicketFile, TicketFileDraft, TicketRelation,
} from "../types";
import { nextMemoSortOrder } from "./memos";
import { ConflictError } from "../types";
import { fileApiUrl } from "./supabase";

function unwrap<T>(data: T | null, error: { message: string } | null): T {
  if (error) throw new Error(error.message);
  if (!data) throw new ConflictError();
  return data;
}

function pageFile(page: Record<string, any>): TicketFile {
  return { id: page.id, workspace_id: page.workspace_id, ticket_id: page.ticket_id, relative_path: page.relative_path, display_name: page.title || page.relative_path, mime_type: "text/markdown", byte_size: new TextEncoder().encode(page.markdown || "").length, kind: "page", content: page.markdown || "", storage_key: null, storage_mode: "database", cloud_status: "database", current_version_id: null, latest_local_sha256: null, revision: page.revision, created_at: page.created_at, updated_at: page.updated_at };
}

function mediaFile(file: Record<string, any>): TicketFile {
  const version = Array.isArray(file.current_version) ? file.current_version[0] : file.current_version;
  return { id: file.id, workspace_id: file.workspace_id, ticket_id: file.ticket_id, relative_path: file.relative_path, display_name: file.display_name, mime_type: file.mime_type, byte_size: Number(file.byte_size), kind: "media", content: "", storage_key: version?.storage_key || null, storage_mode: file.storage_mode, cloud_status: file.cloud_status, current_version_id: file.current_version_id, latest_local_sha256: file.latest_local_sha256, revision: file.revision, created_at: file.created_at, updated_at: file.updated_at };
}

async function fileHash(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}

export class SupabaseRepository implements DataRepository {
  constructor(private readonly client: SupabaseClient, private readonly workspaceId: string) {}

  async getDashboard(date: string): Promise<DashboardData> {
    const [actions, daily, memos, ticketCatalog] = await Promise.all([
      this.client.from("actions").select("*").eq("workspace_id", this.workspaceId).order("sort_order"),
      this.client.from("daily_notes").select("*").eq("workspace_id", this.workspaceId).eq("note_date", date).maybeSingle(),
      this.client.from("memos").select("*").eq("workspace_id", this.workspaceId).order("sort_order").order("updated_at", { ascending: false }),
      this.client.from("ticket_catalog").select("*").eq("workspace_id", this.workspaceId).order("status").order("title"),
    ]);
    for (const result of [actions, daily, memos, ticketCatalog]) if (result.error) throw new Error(result.error.message);
    return {
      tickets: [],
      ticketCatalog: (ticketCatalog.data || []) as TicketCatalog[],
      ticketFiles: [],
      ticketRelations: [],
      actions: (actions.data || []) as Action[],
      daily: daily.data as DailyNote | null,
      memos: (memos.data || []) as Memo[],
    };
  }

  async createAction(input: ActionDraft): Promise<Action> {
    const existing = await this.client.from("actions").select("sort_order").eq("workspace_id", this.workspaceId).order("sort_order", { ascending: true }).limit(1).maybeSingle();
    if (existing.error) throw new Error(existing.error.message);
    const sortOrder = (existing.data?.sort_order ?? 0) - 1;
    const { data, error } = await this.client.from("actions").insert({ ...input, workspace_id: this.workspaceId, sort_order: sortOrder }).select().single();
    return unwrap(data as Action | null, error);
  }

  async updateAction(id: string, revision: number, input: Partial<ActionDraft> & { completed?: boolean }): Promise<Action> {
    const { completed, ...fields } = input;
    const update: Record<string, unknown> = { ...fields };
    if (completed !== undefined) update.completed_at = completed ? new Date().toISOString() : null;
    const { data, error } = await this.client.from("actions").update(update).eq("workspace_id", this.workspaceId).eq("id", id).eq("revision", revision).select().maybeSingle();
    // Completion is a desired state, independent of edits to the action's other fields.
    // Retry only this narrow operation; normal edits must retain conflict protection.
    if (!error && !data && completed !== undefined && Object.keys(fields).length === 0) {
      for (let attempt = 0; attempt < 2; attempt++) {
        const latestResult = await this.client.from("actions").select("*").eq("workspace_id", this.workspaceId).eq("id", id).maybeSingle();
        if (latestResult.error) throw new Error(latestResult.error.message);
        if (!latestResult.data) throw new Error("アクションが見つかりません。削除された可能性があります。");
        const latest = latestResult.data as Action;
        if (Boolean(latest.completed_at) === completed) return latest;
        const retried = await this.client.from("actions").update(update).eq("workspace_id", this.workspaceId).eq("id", id).eq("revision", latest.revision).select().maybeSingle();
        if (retried.error) throw new Error(retried.error.message);
        if (retried.data) return retried.data as Action;
      }
    }
    return unwrap(data as Action | null, error);
  }

  async reorderActions(actionIds: string[]): Promise<void> {
    const { error } = await this.client.rpc("reorder_actions", { target_workspace_id: this.workspaceId, ordered_ids: actionIds });
    if (error) throw new Error(error.message);
  }

  async moveAction(action: Pick<Action, "id" | "revision">, destination: ActionMoveDestination): Promise<void> {
    const { error } = await this.client.rpc("move_action", {
      target_workspace_id: this.workspaceId,
      moved_id: action.id,
      expected_moved_revision: action.revision,
      destination_id: destination.target_id,
      expected_destination_revision: destination.target_revision,
      placement: destination.kind,
    });
    if (error) throw new Error(error.message);
  }

  async changeActionTicketContext(action: Pick<Action, "id" | "revision">, children: Pick<Action, "id" | "revision">[], next: { ticket_key: string | null; project_name: string; context: string }): Promise<number> {
    const { data, error } = await this.client.rpc("change_action_ticket_context", {
      target_workspace_id: this.workspaceId,
      target_action_id: action.id,
      expected_action_revision: action.revision,
      expected_child_ids: children.map((child) => child.id),
      expected_child_revisions: children.map((child) => child.revision),
      next_ticket_key: next.ticket_key,
      next_project_name: next.project_name,
      next_context: next.context,
    });
    if (error) throw new Error(error.message);
    if (data === null || data === undefined) throw new ConflictError();
    return Number(data);
  }

  async changeActionLane(action: Pick<Action, "id" | "revision">, nextLane: Action["lane"], destination?: { id: string; revision: number; placement: "before" | "after" }): Promise<number> {
    const { data, error } = await this.client.rpc("change_action_lane", {
      target_workspace_id: this.workspaceId,
      target_action_id: action.id,
      expected_action_revision: action.revision,
      expected_child_ids: [],
      expected_child_revisions: [],
      next_lane: nextLane,
      destination_id: destination?.id ?? null,
      expected_destination_revision: destination?.revision ?? null,
      placement: destination?.placement ?? "after",
    });
    if (error) throw new Error(error.message);
    if (data === null || data === undefined) throw new ConflictError();
    return Number(data);
  }

  async scheduleActionsForDate(actions: Pick<Action, "id" | "revision">[], date: string): Promise<void> {
    const { error } = await this.client.rpc("schedule_actions_for_date", {
      target_workspace_id: this.workspaceId,
      target_date: date,
      target_ids: actions.map((action) => action.id),
      expected_revisions: actions.map((action) => action.revision),
    });
    if (error) throw new Error(error.message);
  }

  async createTicket(input: TicketDraft): Promise<Ticket> {
    const { data, error } = await this.client.from("tickets").insert({ ...input, workspace_id: this.workspaceId }).select().single();
    return unwrap(data as Ticket | null, error);
  }

  async updateTicket(id: string, revision: number, input: Partial<TicketDraft>): Promise<Ticket> {
    const { data, error } = await this.client.from("tickets").update(input).eq("workspace_id", this.workspaceId).eq("id", id).eq("revision", revision).select().maybeSingle();
    return unwrap(data as Ticket | null, error);
  }

  async createTicketFile(input: TicketFileDraft): Promise<TicketFile> {
    const { data, error } = await this.client.from("ticket_pages").insert({ workspace_id: this.workspaceId, ticket_id: input.ticket_id, relative_path: input.relative_path, title: input.title, markdown: input.content }).select().single();
    const page = unwrap(data, error);
    return pageFile(page);
  }

  async updateTicketFile(id: string, revision: number, input: Partial<TicketFileDraft>): Promise<TicketFile> {
    const update: Record<string, unknown> = {};
    if (input.relative_path !== undefined) update.relative_path = input.relative_path;
    if (input.title !== undefined) update.title = input.title;
    if (input.content !== undefined) update.markdown = input.content;
    const { data, error } = await this.client.from("ticket_pages").update(update).eq("workspace_id", this.workspaceId).eq("id", id).eq("revision", revision).select().maybeSingle();
    const page = unwrap(data, error);
    return pageFile(page);
  }

  async deleteTicketFile(file: TicketFile): Promise<void> {
    const query = file.kind === "page"
      ? this.client.from("ticket_pages").delete({ count: "exact" })
      : this.client.from("ticket_files").update({ deleted_at: new Date().toISOString(), cloud_status: "deleted" }, { count: "exact" });
    const { error, count } = await query.eq("workspace_id", this.workspaceId).eq("id", file.id).eq("revision", file.revision);
    if (error) throw new Error(error.message);
    if (!count) throw new ConflictError();
  }

  async uploadTicketFile(ticketId: string, file: File): Promise<TicketFile> {
    if (!fileApiUrl) throw new Error("ファイル保存先が未設定です。VITE_FILE_API_URLを設定してください。");
    if (file.size > 104857600) throw new Error("初版では100MBを超えるファイルをアップロードできません。");
    const id = crypto.randomUUID();
    const sha256 = await fileHash(file);
    const inserted = await this.client.from("ticket_files").insert({ id, workspace_id: this.workspaceId, ticket_id: ticketId, relative_path: file.name, display_name: file.name, mime_type: file.type || "application/octet-stream", byte_size: file.size, storage_mode: "snapshot", cloud_status: "uploading", latest_local_sha256: sha256 }).select("*,current_version:file_versions!ticket_files_current_version_id_fkey(*)").single();
    if (inserted.error) throw new Error(inserted.error.message);
    try {
      const session = await this.client.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("ログインの有効期限が切れました。");
      const versionId = crypto.randomUUID();
      const response = await fetch(`${fileApiUrl.replace(/\/$/, "")}/v1/workspaces/${this.workspaceId}/files/${id}/versions/${versionId}`, { method: "PUT", headers: { authorization: `Bearer ${token}`, "content-type": file.type || "application/octet-stream", "x-daily-desk-sha256": sha256 }, body: file });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `アップロードに失敗しました (${response.status})`);
      return mediaFile({ ...payload.file, current_version: payload.version });
    } catch (error) {
      await this.client.from("ticket_files").update({ cloud_status: "error" }).eq("workspace_id", this.workspaceId).eq("id", id);
      throw error;
    }
  }

  async downloadTicketFile(file: TicketFile): Promise<Blob> {
    if (file.kind !== "media" || !file.current_version_id) throw new Error("クラウドに保存されたファイルがありません。");
    if (!fileApiUrl) throw new Error("ファイル保存先が未設定です。VITE_FILE_API_URLを設定してください。");
    const session = await this.client.auth.getSession();
    const token = session.data.session?.access_token;
    if (!token) throw new Error("ログインの有効期限が切れました。");
    const response = await fetch(`${fileApiUrl.replace(/\/$/, "")}/v1/workspaces/${this.workspaceId}/files/${file.id}/versions/${file.current_version_id}`, { headers: { authorization: `Bearer ${token}` } });
    if (!response.ok) { const payload = await response.json().catch(() => ({})); throw new Error(payload.error || "ファイルを取得できません。"); }
    return response.blob();
  }

  async saveDaily(date: string, markdown: string, revision?: number): Promise<DailyNote> {
    if (revision) {
      const { data, error } = await this.client.from("daily_notes").update({ markdown }).eq("workspace_id", this.workspaceId).eq("note_date", date).eq("revision", revision).select().maybeSingle();
      return unwrap(data as DailyNote | null, error);
    }
    const { data, error } = await this.client.from("daily_notes").insert({ workspace_id: this.workspaceId, note_date: date, markdown }).select().single();
    return unwrap(data as DailyNote | null, error);
  }

  async createMemo(input: MemoDraft): Promise<Memo> {
    const existing = await this.client.from("memos").select("*").eq("workspace_id", this.workspaceId);
    if (existing.error) throw new Error(existing.error.message);
    const { data, error } = await this.client.from("memos").insert({ ...input, workspace_id: this.workspaceId, sort_order: nextMemoSortOrder((existing.data || []) as Memo[]) }).select().single();
    return unwrap(data as Memo | null, error);
  }

  async updateMemo(id: string, revision: number, input: Partial<MemoDraft>): Promise<Memo> {
    const { data, error } = await this.client.from("memos").update(input).eq("workspace_id", this.workspaceId).eq("id", id).eq("revision", revision).select().maybeSingle();
    return unwrap(data as Memo | null, error);
  }

  async reorderMemos(memoIds: string[]): Promise<void> {
    const { error } = await this.client.rpc("reorder_memos", { target_workspace_id: this.workspaceId, ordered_ids: memoIds });
    if (error) throw new Error(error.message);
  }

  async trash(entity: "ticket" | "action" | "memo", record: Ticket | Action | Memo): Promise<void> {
    const { error } = await this.client.rpc("trash_entity", {
      target_workspace_id: this.workspaceId,
      target_type: entity,
      target_id: record.id,
      expected_revision: record.revision,
    });
    if (error) throw new Error(error.message);
  }

  subscribe(onChange: () => void): () => void {
    const channel: RealtimeChannel = this.client.channel(`daily-desk:${this.workspaceId}`);
    let refreshTimer: number | null = null;
    const scheduleRefresh = () => {
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        onChange();
      }, 180);
    };
    for (const table of ["actions", "daily_notes", "memos", "ticket_catalog"]) {
      channel.on("postgres_changes", { event: "*", schema: "public", table, filter: `workspace_id=eq.${this.workspaceId}` }, scheduleRefresh);
    }
    channel.subscribe();
    return () => {
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      void this.client.removeChannel(channel);
    };
  }
}
